import { jsx, jsxs } from 'react/jsx-runtime';
import { useContext, useState, useCallback, useEffect } from 'react';
import { useImages, useFallbackImage, useErrorForMissingImageProps, ScreenReaderOnlyImg, buildSourceTags, buildImagesByBreakpoint } from '@nicollet/common';
import { AppContext } from '../AppContext/index.js';
import { LazyLoad } from './LazyLoad.js';
import { FallbackImage } from './FallbackImage.js';
import { StyledAspectRatio, FadeInPicture } from './styles.js';

const getFinalImagesNonOptimized = ({
  imagesByBreakpoint,
  actualImages,
  actualImagesByBreakpoint
}) => {
  const finalImagesByBreakpoint = imagesByBreakpoint !== null && imagesByBreakpoint !== void 0 ? imagesByBreakpoint : actualImagesByBreakpoint;
  const finalImagesNonOptimized = (actualImages === null || actualImages === void 0 ? void 0 : actualImages.length) ? actualImages : buildImagesByBreakpoint(finalImagesByBreakpoint);
  return finalImagesNonOptimized;
};

const useLoadingState = ({
  onLoad,
  onError,
  onErrorRenderingImage
}) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const handleLoad = useCallback(() => {
    if (!hasLoaded) {
      setHasLoaded(true);
    }

    if (onLoad) {
      onLoad();
    }
  }, [hasLoaded, onLoad]);
  const handleError = useCallback(e => {
    onErrorRenderingImage();

    if (hasError) {
      setHasError(true);
    }

    if (onError) {
      onError(e);
    }
  }, [hasError, onError, onErrorRenderingImage]);
  useEffect(() => {
    handleLoad(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    handleLoad,
    handleError,
    hasLoaded,
    hasError
  };
};

const PictureLazy = props => {
  var _a;

  const {
    additionalParameters,
    alt = '',
    baseUrl,
    className,
    fallbackImageUrl = '',
    height,
    images = [],
    imagesByBreakpoint,
    imageType,
    isCdnImage,
    // set as true for backwards compatibility reasons
    isFallbackImageDisabled = true,
    lazyLoad = false,
    noAnimation = false,
    onError,
    onLoad,
    resolutionsByBreakpoint,
    src,
    width,
    baseResolutionHeight = typeof height === 'number' ? height : undefined,
    baseResolutionWidth = typeof width === 'number' ? width : undefined,
    ...otherProps
  } = props;
  const appContext = useContext(AppContext.Context);
  const {
    hasWebpSupport = false
  } = appContext;
  const {
    actualImages,
    actualImagesByBreakpoint,
    imageParams
  } = useImages({
    additionalParameters,
    baseResolutionHeight,
    baseResolutionWidth,
    baseUrl,
    hasWebpSupport,
    images,
    imageType,
    isCdnImage,
    resolutionsByBreakpoint,
    src
  });
  const {
    fallbackImages,
    fallbackImagesByBreakpoint,
    hasFallbackImage,
    onErrorRenderingImage
  } = useFallbackImage({
    fallbackImageUrl,
    hasWebpSupport,
    imageParams,
    resolutionsByBreakpoint,
    additionalParameters
  });
  const {
    hasLoaded,
    hasError,
    handleLoad,
    handleError
  } = useLoadingState({
    onLoad,
    onError,
    onErrorRenderingImage
  });
  useErrorForMissingImageProps(imageParams, images, resolutionsByBreakpoint);
  const finalImagesNonOptimized = getFinalImagesNonOptimized({
    actualImages,
    actualImagesByBreakpoint,
    imagesByBreakpoint
  });

  const lazyPlaceholder = jsx(ScreenReaderOnlyImg, {
    alt: alt
  }, void 0);

  if (!isFallbackImageDisabled && hasFallbackImage) {
    if (fallbackImages) {
      return jsx(PictureLazy, Object.assign({
        height: typeof height === 'number' ? `${height}px` : height,
        images: fallbackImages,
        imagesByBreakpoint: fallbackImagesByBreakpoint,
        src: (_a = imageParams === null || imageParams === void 0 ? void 0 : imageParams.imageSrc) !== null && _a !== void 0 ? _a : src,
        width: typeof width === 'number' ? `${width}px` : width
      }, otherProps), void 0);
    }

    return jsx(FallbackImage, Object.assign({}, otherProps), void 0);
  }

  const hasInvisibleFadeInPicture = typeof window === 'undefined' ? false : !hasLoaded && !hasError;
  const finalSrcTags = buildSourceTags(finalImagesNonOptimized !== null && finalImagesNonOptimized !== void 0 ? finalImagesNonOptimized : [], hasWebpSupport);
  return jsx(LazyLoad, Object.assign({
    className: className,
    enabled: lazyLoad,
    placeholder: lazyPlaceholder
  }, {
    children: jsxs(FadeInPicture, Object.assign({
      isInvisible: hasInvisibleFadeInPicture,
      noAnimation: noAnimation,
      onError: handleError,
      onLoad: handleLoad
    }, otherProps, {
      children: [finalSrcTags, jsx("img", {
        alt: alt,
        height: typeof height === 'boolean' ? undefined : height,
        src: src,
        width: typeof width === 'boolean' ? undefined : width
      }, void 0)]
    }), void 0)
  }), void 0);
};

PictureLazy.displayName = 'PictureLazy';

const Picture = props => {
  const {
    aspectRatio,
    className,
    height,
    width,
    objectFit,
    AspectRatioComponent = StyledAspectRatio
  } = props;

  if (AspectRatioComponent === null) {
    return jsx(PictureLazy, Object.assign({}, props, {
      className: className,
      height: height,
      width: width
    }), void 0);
  }

  return jsx(AspectRatioComponent, Object.assign({
    className: className,
    height: height,
    objectFit: objectFit,
    ratio: aspectRatio,
    width: width
  }, {
    children: jsx(PictureLazy, Object.assign({}, props), void 0)
  }), void 0);
};

Picture.displayName = 'Picture';

export { Picture };
